import type { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateCapsule } from "@babylonjs/core/Meshes/Builders/capsuleBuilder";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { CreateIcoSphere } from "@babylonjs/core/Meshes/Builders/icoSphereBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import {
  Action,
  Combat,
  Movement,
  attackDef,
  attackPhase,
  groundHeightAt,
  type ArenaDefinition,
  type AttackDef,
  type CharacterManifest,
  type FighterKit,
} from "@arena/shared";
import { ARENA_COLORS, COMBAT_COLORS, fighterCss, hex } from "../palette.ts";
import { GhostView } from "./ghostView.ts";

/** Pose the view is drawn at this frame (already interpolated / predicted). */
export interface FighterPose {
  x: number;
  y: number;
  z: number;
  fx: number;
  fz: number;
  vy: number;
  grounded: boolean;
  dashing: boolean;
  connected: boolean;
  action: number;
  actionTick: number;
  hitstun: number;
  anchorTicks?:number;
  slamReady: boolean;
  shielded: boolean;
  /** Countdown freeze: no stun effects. */
  frozen: boolean;
  knockedOut?: boolean;
  grabbed?: boolean;
  /** Cleopatra's venom: a green pulse on the sprite (D45). */
  poisoned?: boolean;
  /** Cleopatra's charm: a pink blush on the sprite. */
  charmed?: boolean;
  /** Maria Antonietta's whirlwind (`spinTicks`): the spin loop. */
  spinning?: boolean;
}

const CHEST = Movement.height * 0.62;
const FLASH_SECONDS = 0.14;
const HEAVY_GLOW = hex(COMBAT_COLORS.heavy);
const SLAM_GLOW = hex(COMBAT_COLORS.slam).scale(0.6);

/**
 * Body proportions per build. Only the drawing changes: every champion has
 * the same collision cylinder (`Movement.radius`, `Movement.height`), so
 * what you see is what can be hit.
 */
const BUILDS: Record<CharacterManifest["look"]["build"], { width: number; height: number }> = {
  heavy: { width: 1.18, height: 0.96 },
  slim: { width: 0.84, height: 1.04 },
};

/**
 * Placeholder fighter until the GLB models exist: a faceted capsule with a
 * visor showing the facing, the champion's build and accent piece (shoulder
 * block for the brawler, crest for the mobile fighter), a blob shadow and a
 * ring under the local player. Combat reads at a glance: a fist that winds
 * up, strikes and pulls back, the hitbox drawn on the floor while it is live,
 * a white flash when hit, a gold halo when the Super Slam is ready, its real
 * reach and shape on the floor while it winds up, a bubble during the
 * anti-chain protection, and an accent glow while an ability is armoured.
 */
export class PlayerView {
  readonly root: TransformNode;
  readonly label: HTMLDivElement;
  private readonly body: Mesh;
  private readonly bodyMaterial: StandardMaterial;
  private readonly accentMaterial: StandardMaterial;
  private readonly shoulders: Mesh;
  private readonly crest: Mesh;
  private readonly shadow: Mesh;
  /** Local player: thick ring in its colour. */
  private readonly ring: Mesh | null;
  private readonly ringMaterial: StandardMaterial | null;
  /** Team match, everyone else: thin ring in the team colour, readable from any camera angle. */
  private readonly teamRing: Mesh | null;
  private readonly teamRingMaterial: StandardMaterial | null;
  private readonly fist: Mesh;
  private readonly fistMaterial: StandardMaterial;
  private readonly arcs = new Map<number, Mesh>();
  private readonly arcMaterial: StandardMaterial;
  /** Floor marker of the Super Slam reach; rebuilt when the champion changes. */
  private slamZone: Mesh;
  private readonly slamZoneMaterial: StandardMaterial;
  private readonly halo: Mesh;
  private readonly bubble: Mesh;
  private arena: ArenaDefinition;
  private readonly scene: Scene;
  private readonly colorIndex: number;
  private baseColor: Color3;
  private stunnedColor: Color3;
  private team = -1;
  /** False when another view draws the body (the 2D sprite prototype). */
  private bodyVisible = true;
  private kit: FighterKit;
  private championId = "";
  private build = BUILDS.heavy;
  private accentGlow = Color3.Black();
  private wasGrounded = true;
  /** Landing squash, decays to 0. */
  private squash = 0;
  private lastHitstun = 0;
  private flash = 0;
  private time = 0;
  /** Drawn instead of the body while the player has no champion (D49). */
  private ghost: GhostView | null = null;

  constructor(
    scene: Scene,
    arena: ArenaDefinition,
    name: string,
    colorIndex: number,
    isLocal: boolean,
    labels: HTMLElement,
    champion: CharacterManifest,
    kit: FighterKit,
  ) {
    this.scene = scene;
    this.arena = arena;
    this.kit = kit;
    this.colorIndex = colorIndex;
    const color = hex(fighterCss(colorIndex, 0));
    this.baseColor = color;
    this.stunnedColor = color.scale(0.8);
    this.root = new TransformNode(`fighter-${name}`, scene);

    this.bodyMaterial = new StandardMaterial(`fighter-${name}`, scene);
    this.bodyMaterial.diffuseColor = color.clone();
    this.bodyMaterial.specularColor = Color3.Black();

    this.body = CreateCapsule(
      "body",
      { height: Movement.height, radius: Movement.radius, tessellation: 8, subdivisions: 1, capSubdivisions: 2 },
      scene,
    );
    this.body.convertToFlatShadedMesh();
    this.body.material = this.bodyMaterial;
    this.body.position.y = Movement.height / 2;
    this.body.parent = this.root;

    const visor = CreateBox("visor", { width: 0.56, height: 0.2, depth: 0.22 }, scene);
    visor.material = unlit(scene, "visor", "#fff4e0", 0.35);
    visor.position.set(0, Movement.height * 0.78 - Movement.height / 2, Movement.radius * 0.82);
    visor.parent = this.body;

    // Champion pieces, in the accent colour: only the one of the current build is shown.
    this.accentMaterial = new StandardMaterial("accent", scene);
    this.accentMaterial.specularColor = Color3.Black();
    this.shoulders = CreateBox("shoulders", { width: Movement.radius * 2.5, height: 0.26, depth: 0.5 }, scene);
    this.shoulders.material = this.accentMaterial;
    this.shoulders.position.set(0, Movement.height * 0.66 - Movement.height / 2, 0);
    this.shoulders.parent = this.body;
    this.crest = CreateBox("crest", { width: 0.1, height: 0.42, depth: 0.62 }, scene);
    this.crest.material = this.accentMaterial;
    this.crest.position.set(0, Movement.height / 2 + 0.08, -0.12);
    this.crest.parent = this.body;

    this.fist = CreateBox("fist", { size: 0.3 }, scene);
    this.fistMaterial = new StandardMaterial("fist", scene);
    this.fistMaterial.diffuseColor = color.scale(0.75);
    this.fistMaterial.specularColor = Color3.Black();
    this.fist.material = this.fistMaterial;
    this.fist.parent = this.root;
    this.fist.setEnabled(false);

    this.arcMaterial = unlit(scene, "arc", COMBAT_COLORS.hitbox, 1);
    this.arcMaterial.alpha = 0.4;
    this.arcMaterial.backFaceCulling = false;

    this.slamZoneMaterial = unlit(scene, "slam-zone", COMBAT_COLORS.slam, 1);
    this.slamZoneMaterial.backFaceCulling = false;
    this.slamZone = this.createSlamZone(kit.moves.superSlam);

    this.halo = CreateTorus("halo", { diameter: 1.45, thickness: 0.07, tessellation: 32 }, scene);
    this.halo.material = unlit(scene, "halo", COMBAT_COLORS.slam, 1);
    this.halo.setEnabled(false);

    this.bubble = CreateIcoSphere("bubble", { radius: Movement.height * 0.62, subdivisions: 1, flat: true }, scene);
    const bubbleMaterial = unlit(scene, "bubble", COMBAT_COLORS.shield, 1);
    bubbleMaterial.alpha = 0.22;
    this.bubble.material = bubbleMaterial;
    this.bubble.position.y = Movement.height / 2;
    this.bubble.parent = this.root;
    this.bubble.setEnabled(false);

    this.shadow = CreateDisc("shadow", { radius: Movement.radius * 1.1, tessellation: 10 }, scene);
    this.shadow.rotation.x = Math.PI / 2;
    const shadowMaterial = unlit(scene, "shadow", ARENA_COLORS.shadow, 1);
    shadowMaterial.alpha = 0.45;
    this.shadow.material = shadowMaterial;

    if (isLocal) {
      this.ring = CreateTorus("ring", { diameter: 1.35, thickness: 0.09, tessellation: 18 }, scene);
      this.ringMaterial = new StandardMaterial("ring", scene);
      this.ringMaterial.disableLighting = true;
      this.ringMaterial.emissiveColor = color.clone();
      this.ring.material = this.ringMaterial;
      this.teamRing = null;
      this.teamRingMaterial = null;
    } else {
      this.ring = null;
      this.ringMaterial = null;
      this.teamRing = CreateTorus("team-ring", { diameter: 1.2, thickness: 0.05, tessellation: 16 }, scene);
      this.teamRingMaterial = new StandardMaterial("team-ring", scene);
      this.teamRingMaterial.disableLighting = true;
      this.teamRing.material = this.teamRingMaterial;
      this.teamRing.setEnabled(false);
    }

    this.label = document.createElement("div");
    this.label.className = isLocal ? "label self" : "label";
    this.label.textContent = name;
    labels.appendChild(this.label);

    this.setChampion(champion, kit);
  }

  get position(): Vector3 {
    return this.root.position;
  }

  /** Height of the name tag above the feet. */
  get labelHeight(): number {
    return 2.15;
  }

  /** World position of the chest, for hit sparks. */
  chest(out: Vector3): Vector3 {
    return out.set(this.root.position.x, this.root.position.y + CHEST, this.root.position.z);
  }

  /** Hides the 3D body, fist and champion pieces; rings, markers and the name tag stay. */
  hideBody(): void {
    this.bodyVisible = false;
    this.body.setEnabled(false);
    this.fist.setEnabled(false);
  }

  /**
   * Team of the fighter (0 = free-for-all): body, rings and name tag take the
   * team colour. Cheap to call every frame: it only acts on a change.
   */
  setTeam(team: number): void {
    if (team === this.team) return;
    this.team = team;
    const css = fighterCss(this.colorIndex, team);
    const color = hex(css);
    this.baseColor = color;
    this.stunnedColor = color.scale(0.8);
    this.fistMaterial.diffuseColor = color.scale(0.75);
    this.ringMaterial?.emissiveColor.copyFrom(color);
    this.teamRingMaterial?.emissiveColor.copyFrom(color);
    this.label.style.setProperty("--team", css);
    this.label.classList.toggle("team", team !== 0);
  }

  /** Switches the drawn champion (lobby choice). Cheap to call every frame: it only acts on a change. */
  setChampion(champion: CharacterManifest, kit: FighterKit): void {
    if (champion.id === this.championId && kit===this.kit) return;
    this.championId = champion.id;
    this.kit = kit;
    this.build = BUILDS[champion.look.build];
    const accent = hex(champion.look.accent);
    this.accentMaterial.diffuseColor = accent;
    this.accentGlow = accent.scale(0.55);
    this.shoulders.setEnabled(champion.look.build === "heavy");
    this.crest.setEnabled(champion.look.build === "slim");
    for (const arc of this.arcs.values()) arc.dispose();
    this.arcs.clear();
    this.slamZone.dispose();
    this.slamZone = this.createSlamZone(kit.moves.superSlam);
  }

  update(pose: FighterPose, dt: number): void {
    this.time += dt;
    this.root.position.set(pose.x, pose.y, pose.z);
    this.root.setEnabled(!pose.knockedOut);
    if (pose.fx !== 0 || pose.fz !== 0) this.root.rotation.y = Math.atan2(pose.fx, pose.fz);
    // No champion yet: a ghost instead of the body (D49).
    const ghost = this.kit.ghost === true;
    if (ghost && !this.ghost) this.ghost = new GhostView(this.scene, this.baseColor);
    if (!ghost && this.ghost) {
      this.ghost.dispose();
      this.ghost = null;
    }
    this.body.setEnabled(this.bodyVisible && !ghost);
    this.ghost?.update(pose.x, pose.y, pose.z, pose.fx, pose.fz, dt);

    // Squash and stretch: cheap feedback for jumps, landings and dashes.
    if (pose.grounded && !this.wasGrounded) this.squash = 1;
    this.wasGrounded = pose.grounded;
    this.squash = Math.max(0, this.squash - dt * 7);
    const airStretch = pose.grounded ? 0 : Math.min(Math.abs(pose.vy) / 30, 0.18);
    const stretchY = (1 + airStretch - this.squash * 0.22 - (pose.dashing ? 0.12 : 0)) * this.build.height;
    const widen = this.build.width / Math.sqrt(Math.max(stretchY / this.build.height, 0.5));
    this.body.scaling.set(widen, stretchY, pose.dashing ? widen * 1.25 : widen);
    this.body.position.y = (Movement.height / 2) * stretchY;

    this.updateHitReaction(pose, dt);
    this.updateAttack(pose);

    this.bodyMaterial.alpha = pose.connected ? 1 : 0.4;
    this.label.classList.toggle("offline", !pose.connected);

    this.bubble.setEnabled(pose.shielded);

    const ground = groundHeightAt(this.arena, pose.x, pose.z, pose.y + 0.05);
    const visible = ground !== null && !pose.knockedOut;
    this.halo.setEnabled(pose.slamReady && visible);
    if (visible) this.halo.position.set(pose.x, ground + 0.045, pose.z);
    const readyPulse = 1 + 0.06 * Math.sin(this.time * 5);
    this.halo.scaling.set(readyPulse, 1, readyPulse);
    this.shadow.setEnabled(visible);
    this.ring?.setEnabled(visible);
    this.teamRing?.setEnabled(visible && this.team > 0);
    if (visible) {
      const height = Math.max(0, pose.y - ground);
      const scale = Math.max(0.45, 1 - height / 6);
      this.shadow.position.set(pose.x, ground + 0.012, pose.z);
      this.shadow.scaling.set(scale, scale, scale);
      this.ring?.position.set(pose.x, ground + 0.02, pose.z);
      this.teamRing?.position.set(pose.x, ground + 0.02, pose.z);
    }

    const winding = pose.action === Action.SuperSlam && pose.actionTick < Combat.superStartup;
    this.slamZone.setEnabled(winding && visible);
    if (winding && ground !== null) {
      // The reach closes in on the caster as the Super Slam winds up.
      const progress = pose.actionTick / Combat.superStartup;
      this.slamZone.position.set(pose.x, ground + 0.04, pose.z);
      this.slamZone.rotation.y = this.root.rotation.y;
      const s = 1.15 - 0.15 * progress;
      this.slamZone.scaling.set(s, 1, s);
      this.slamZoneMaterial.alpha = 0.3 + 0.5 * progress;
    }
  }

  setName(name: string): void {
    this.label.textContent = name;
  }

  /** The world under the fighter's shadow, after a change of map (D49). */
  setArena(arena: ArenaDefinition): void {
    this.arena = arena;
  }

  dispose(): void {
    this.ghost?.dispose();
    this.halo.dispose(false, true);
    this.root.dispose(false, true);
    this.shadow.dispose(false, true);
    this.slamZone.dispose(false, true);
    this.ring?.dispose(false, true);
    this.teamRing?.dispose(false, true);
    this.label.remove();
  }

  private updateHitReaction(pose: FighterPose, dt: number): void {
    if (pose.hitstun > this.lastHitstun && !pose.frozen) this.flash = FLASH_SECONDS;
    this.lastHitstun = pose.hitstun;
    this.flash = Math.max(0, this.flash - dt);

    const stunned = pose.hitstun > 0 && !pose.frozen;
    const move = attackDef(this.kit.table, pose.action);
    // Armoured opening of an ability (not the Super Slam, which has its own telegraph).
    const armored = move !== undefined && pose.action !== Action.SuperSlam && pose.actionTick < move.armorTicks;
    // Lean back while stunned, wobbling, so it reads even in silhouette; lean in during a rush.
    const rushing = move !== undefined && move.travel > 0 && pose.actionTick < move.startup + move.active;
    this.body.rotation.x = stunned ? -0.32 + Math.sin(this.time * 30) * 0.05 : rushing ? 0.3 : 0;
    if (this.flash > 0) this.bodyMaterial.emissiveColor.set(0.9, 0.9, 0.9);
    else if (stunned) this.bodyMaterial.emissiveColor.set(0.18, 0.18, 0.18);
    else if (armored) this.bodyMaterial.emissiveColor.copyFrom(this.accentGlow);
    else this.bodyMaterial.emissiveColor.set(0, 0, 0);
    this.bodyMaterial.diffuseColor.copyFrom(stunned ? this.stunnedColor : this.baseColor);
  }

  private updateAttack(pose: FighterPose): void {
    const def = attackDef(this.kit.table, pose.action);
    const normal = def && def.id !== Action.SuperSlam ? def : undefined;
    this.fist.setEnabled(this.bodyVisible && def !== undefined);
    // Sprite fighters show drawn attack effects; the greybox discs obscure their poses.
    for (const [id, arc] of this.arcs) arc.setEnabled(this.bodyVisible && id === normal?.id && isLive(normal, pose.actionTick));

    if (pose.action === Action.SuperSlam) {
      // Both fists raised, then slammed down.
      const live = pose.actionTick >= Combat.superStartup;
      this.fist.position.set(0, live ? 0.25 : Movement.height + 0.35, Movement.radius * 0.6);
      this.fist.scaling.setAll(1.8);
      this.fistMaterial.emissiveColor.copyFrom(SLAM_GLOW);
      return;
    }
    if (!normal) return;

    const heavy = normal.id === Action.Throw;
    const ability = normal.id === Action.Special1 || normal.id === Action.Special2;
    const phase = attackPhase(normal, pose.actionTick);
    const reach = Math.max(normal.range, 0.6) * 0.75;
    let forward: number;
    if (phase === "startup") {
      // Wind up: pull back (heavy attacks and abilities glow, it is the telegraph).
      forward = Movement.radius * 0.2 - (pose.actionTick / normal.startup) * 0.25;
    } else if (phase === "active") {
      forward = Movement.radius + reach;
    } else {
      const t = (pose.actionTick - normal.startup - normal.active) / Math.max(1, normal.recovery);
      forward = Movement.radius + reach * (1 - t);
    }
    const uppercut = normal.lift > 0 && phase !== "startup";
    this.fist.position.set(heavy || ability ? 0 : 0.18, uppercut ? CHEST + 0.3 : CHEST, forward);
    this.fist.scaling.setAll(heavy ? 1.6 : ability ? 1.3 : 1);
    const telegraph = (heavy || ability) && phase === "startup" ? 0.5 + 0.4 * Math.sin(this.time * 40) : 0;
    if (ability) this.accentGlow.scaleToRef(telegraph * 1.6, this.fistMaterial.emissiveColor);
    else HEAVY_GLOW.scaleToRef(telegraph, this.fistMaterial.emissiveColor);

    // Ranged moves have no melee hitbox to draw: the projectile is the hitbox.
    if (this.bodyVisible && !normal.projectile && isLive(normal, pose.actionTick) && !this.arcs.has(normal.id)) {
      const arc = this.createArc(normal);
      this.arcs.set(normal.id, arc);
    }
  }

  /** Floor sector matching the hitbox: range plus body radius, arc centred on the facing. */
  private createArc(def: AttackDef): Mesh {
    const fraction = def.arcDeg / 360;
    const arc = CreateDisc(`arc-${def.id}`, { radius: def.range + Movement.radius, arc: fraction, tessellation: 18 }, this.scene);
    arc.material = this.arcMaterial;
    arc.rotation.x = Math.PI / 2;
    // The disc starts at +X and sweeps towards +Z: centre the sweep on the facing (+Z).
    arc.rotation.y = fraction * Math.PI - Math.PI / 2;
    arc.position.y = 0.05;
    arc.parent = this.root;
    return arc;
  }

  /**
   * The Super Slam's reach on the floor: a ring for a full circle, a sector
   * along the facing for a cone. Not parented, so it stays flat on the ground
   * under a jumping caster.
   */
  private createSlamZone(def: AttackDef): Mesh {
    const reach = def.range + Movement.radius;
    let zone: Mesh;
    if (def.arcDeg >= 360) {
      zone = CreateTorus("slam-zone", { diameter: reach * 2, thickness: 0.12, tessellation: 32 }, this.scene);
    } else {
      const fraction = def.arcDeg / 360;
      zone = CreateDisc("slam-zone", { radius: reach, arc: fraction, tessellation: 24 }, this.scene);
      zone.rotation.set(Math.PI / 2, fraction * Math.PI - Math.PI / 2, 0);
      zone.bakeCurrentTransformIntoVertices();
    }
    zone.material = this.slamZoneMaterial;
    zone.isPickable = false;
    zone.setEnabled(false);
    return zone;
  }
}

function isLive(def: AttackDef, tick: number): boolean {
  return attackPhase(def, tick) === "active";
}

function unlit(scene: Scene, name: string, color: string, emissive: number): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseColor = hex(color);
  material.emissiveColor = hex(color).scale(emissive);
  material.specularColor = Color3.Black();
  material.disableLighting = emissive >= 1;
  return material;
}
